// contact sources/lookups, indexed by contact
import { OrderedMap, Set } from 'immutable';
import Collection from '../../Models/Collection';
import type { ContactInfoSource, ContactInfoType, ID, ReduxAction } from '../../types';
import Occasion from '@support/Occasion';
import BaseObject from '@models/BaseObject';

export class ContactSource extends BaseObject {
  value: string;
  type?: ContactInfoType;
  source?: ContactInfoSource;
  label?: string;

  constructor(props: any) {
    super(props);
    this.value = props.value;
    this.type = props.type;
    this.source = props.source;
    this.label = props.label;
  }
}

export const selectContactSources =
  (contactID: ID) =>
  (state: any): Collection<ContactSource> =>
    state.data.sources.collection.items.get(String(contactID), new Collection<ContactSource>());

// Collection of ContactSource instances is keyed by contact.id
export interface SourcesState {
  collection: Collection<Collection<ContactSource>>;
}

const initialSourcesState = {
  collection: new Collection<Collection<ContactSource>>(),
};

export function sourcesReducer(
  state: SourcesState = initialSourcesState,
  { type, payload }: ReduxAction,
) {
  const contactID = String(payload?.contactID);

  switch (type) {
    case 'FETCH_ALL_SOURCES_REQUESTED':
      return {
        collection: state.collection.copyWith({
          items: state.collection.items.update(
            contactID,
            new Collection({ isFetching: true }),
            (contactSources) => contactSources.copyWith({ isFetching: true, error: null }),
          ),
        }),
      };

    case 'FETCH_ALL_SOURCES_SUCCEEDED':
      return {
        collection: state.collection.copyWith({
          items: state.collection.items.update(contactID, (contactSources) =>
            contactSources.copyWith({
              items: payload.sources.map((source) => [
                String(source.id),
                new ContactSource(source),
              ]),
              isFetching: false,
              updated: new Occasion(),
            }),
          ),
        }),
      };

    case 'CREATE_SOURCE_REQUESTED':
      return {
        collection: state.collection.copyWith({
          items: state.collection.items.update(
            contactID,
            new Collection({ isFetching: true }),
            (contactSources) =>
              contactSources.copyWith({
                items: contactSources.items.set(
                  'creating',
                  new ContactSource({ value: payload.value }),
                ),
                isFetching: true,
                error: null,
              }),
          ),
        }),
      };

    case 'CREATE_SOURCE_SUCCEEDED':
      const existingSources = state.collection.items.get(contactID, new Collection());
      const existingSourcesIDSet = existingSources.items.keySeq().toSet();
      const updatedSourcesIDSet = Set(
        payload.response?.contact_information?.map((source) => source.id),
      );
      const newSourcesIDSet = updatedSourcesIDSet.subtract(existingSourcesIDSet);
      const newItemsMap = OrderedMap<ID, ContactSource>(
        newSourcesIDSet.valueSeq().map((id) => {
          const source = payload.response.contact_information.find(
            (source) => String(source.id) === String(id),
          );
          return [String(id), new ContactSource(source)];
        }),
      );

      return {
        collection: state.collection.copyWith({
          items: state.collection.items.update(contactID, (contactSources) =>
            contactSources.copyWith({
              items: contactSources.items.delete('creating').merge(newItemsMap),
              isFetching: false,
              updated: new Occasion(),
            }),
          ),
        }),
      };

    case 'CREATE_SOURCE_FAILED':
      return {
        collection: state.collection.copyWith({
          items: state.collection.items.update(contactID, (contactSources) =>
            contactSources.copyWith({
              items: contactSources.items.filter((source) => source.value !== payload.value),
              isFetching: false,
              error: { ...payload.error, requestValue: payload.value },
            }),
          ),
        }),
      };

    case 'UPDATE_SOURCE_REQUESTED':
      return {
        collection: state.collection.copyWith({
          items: state.collection.items.update(contactID, (contactSources) =>
            contactSources.copyWith({
              items: contactSources.items.update(payload.sourceID, (source) =>
                source.copyWith({ label: payload.label }),
              ),
              error: null,
            }),
          ),
        }),
      };

    case 'DELETE_SOURCE_REQUESTED':
      return {
        collection: state.collection.copyWith({
          items: state.collection.items.update(contactID, (contactSources) =>
            contactSources.copyWith({
              items: contactSources.items.filter((source) => source.value !== payload.source.value),
              isFetching: true,
              error: null,
            }),
          ),
        }),
      };

    case 'DELETE_SOURCE_SUCCEEDED':
      return {
        collection: state.collection.copyWith({
          items: state.collection.items.update(contactID, (contactSources) =>
            contactSources.copyWith({
              isFetching: false,
              updated: new Occasion(),
            }),
          ),
        }),
      };

    case 'DELETE_SOURCE_FAILED':
      return {
        collection: state.collection.copyWith({
          items: state.collection.items.set(contactID, payload.initialSourcesCollection),
        }),
      };

    default:
      return state;
  }
}
